import React from 'react';
import Img from 'react-image';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ColorMask from '../ColorMask';
import HeaderCallout from '../typography/HeaderCallout';
import Loading from '../Loading';
import Link from '../../global/NavLink';

const DetailTxt = styled.div`
  color: ${props => props.theme.color.headerSecondary};
  font-size: 12px;
  font-family: ${props => props.theme.font.sourceSans};
  font-weight: ${props => props.theme.fontWeight.bold};
  line-height: 1.25;
  margin: 10px 0 4px 0;
  min-height: 21px;
  padding-bottom: 5px;
  flex: 0 0 auto;
  border-bottom: 1px solid ${props => props.theme.color.borderPrimary};
`;

const Flag = styled.div`
  align-items: center;
  background-color: rgba(200, 200, 200, 0.6);
  display: flex;
  height: 100%;
  justify-content: center;
  position: absolute;
  text-align: center;
  width: 100%;
`;

const FlagText = styled.div`
  color: ${props => props.theme.color.textPrimary};
  font-family: ${props => props.theme.font.sourceSans};
  font-size: 18px;
  font-weight: ${props => props.theme.fontWeight.bold};
`;

const Header = styled.h2`
  color: ${props => props.theme.color.secondary};
  font-size: 16px;
  font-family: ${props => props.theme.font.sourceSans};
  font-weight: ${props => props.theme.fontWeight.black};
  line-height: 1.5;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: -2px 0 0 0;
  flex: 1 0 auto;
  ${props =>
    props.singleLine &&
    `white-space: nowrap;
     text-overflow: ellipsis;
     overflow: hidden;`};
  @media (max-width: ${props => props.theme.deviceWidth.mobilePhone}) {
    line-height: 1.2;
    margin: 8px 0;
  }
`;

const Image = styled(Img)`
  bottom: 0;
  display: block;
  position: absolute;
  top: 0;
  left: 50%;
  max-width: 100%;
  max-height: 100%;
  transform: translate(-50%);
`;

const ImageWrapper = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  flex: 0 0 auto;
  &:after {
    padding-bottom: 100%;
    display: block;
    content: '';
  }
`;

const LinkWrapper = styled(Link)`
  display: block;
  height: 100%;
`;

const Text = styled.p`
  color: ${props => props.theme.color.textPrimary};
  font-size: 14px;
  font-family: ${props => props.theme.font.sourceSans};
  line-height: 1.14;
  margin: 0;
  word-wrap: break-word;
`;

const TxtCallout = styled(HeaderCallout)`
  margin: 4px 0 0 0;
  flex: 0 0 auto;
`;

const TxtDocumentType = styled(HeaderCallout)`
  color: ${props => props.theme.color.textSecondary};
  margin: 4px 0 0 0;
`;

const TxtWrapper = styled.div`
  padding: 0 10px 20px;
  flex: 1 0 auto;
  flex-direction: column;
  display: flex;
`;

const Wrapper = styled.div`
  ${props =>
    props.flat
      ? `background-color: ${props.theme.color.linkPrimary}`
      : `background: none`};
  ${props =>
    props.flat
      ? `box-shadow: 26px 10px 26px 0 ${props.theme.color.shadowPrimary}`
      : `box-shadow: none`};
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;

  @media (max-width: ${props => props.theme.deviceWidth.mobilePhone}) {
    box-shadow: none;
  }
`;

const ItemCard = ({
  archived,
  flat,
  category,
  count,
  countDesc,
  description,
  documentCategory,
  image,
  path,
  singleLineTitle = false,
  sub,
  title,
  type
}) => (
  <LinkWrapper to={path}>
    <Wrapper flat={flat}>
      <ImageWrapper>
        <Image
          alt={title}
          loader={<Loading />}
          src={[
            image,
            type === 'Literature'
              ? 'https://placehold.it/196x252'
              : 'https://placehold.it/269x269'
          ]}
        />
        {archived && (
          <Flag>
            <FlagText>OUT OF PRODUCTION</FlagText>
          </Flag>
        )}
        <ColorMask />
      </ImageWrapper>
      <TxtWrapper>
        {countDesc && (
          <DetailTxt>
            {count} {countDesc}
            {count !== 1 ? 's' : ''}
          </DetailTxt>
        )}
        {!countDesc && <DetailTxt>{sub && sub}</DetailTxt>}
        {title && (
          <Header title={title} singleLine={singleLineTitle}>
            {title}
          </Header>
        )}
        {description && <Text>{description}</Text>}
        {category && <TxtCallout>{category}</TxtCallout>}
        {documentCategory && (
          <TxtDocumentType>{documentCategory}</TxtDocumentType>
        )}
      </TxtWrapper>
    </Wrapper>
  </LinkWrapper>
);

ItemCard.propTypes = {
  flat: PropTypes.bool,
  archived: PropTypes.bool,
  category: PropTypes.string,
  count: PropTypes.number,
  countDesc: PropTypes.string,
  documentCategory: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  path: PropTypes.string.isRequired,
  singleLineTitle: PropTypes.bool,
  sub: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.string
};

export default ItemCard;
